/**
 * The fan's identity, as a plain value derived from Auth0's ID-token claims.
 *
 * Everything here is a pure function of claims, so all of it is exported and all of it is tested.
 * The SDK, `window` and storage live in ./index.js; nothing in this file touches any of them.
 *
 * **This replaces reading identity out of cookies.** Until 2026-09 the site derived "who is this"
 * from `auth0_user_id`, `auth0_team_id` and `auth0_authenticated` — three cookies written by four
 * different places, deleted on every page load by a third-party script, and forgeable by anyone.
 * The claims below arrive inside a token the SDK has already validated, which is the whole point of
 * the move. See docs/subsystems/auth.md.
 */

/** What every reader degrades to. Never a thrown error, never a rejected promise. */
export const NO_FAN = Object.freeze( {
	signedIn: false,
	sub: null,
	email: null,
	clubId: null,
} );

/**
 * The fan's club, out of the claim that does not sound like it holds one.
 *
 * **The Auth0 claim is called `team_name` and it carries the numeric club id.** That naming trap is
 * old and is not ours to fix — `App\Loyalty\FanTeam` reads the same value out of a cookie and
 * `outlier-stats` reads the same claim off `user.team_name` in half a dozen components.
 *
 * Parsed strictly, which is a deliberate improvement on both of them: `FanTeam` casts with `(int)`
 * and `get_team_logo_by_id()` binds with `%d`, so a value that really is a *name* becomes `0` and
 * reads as "this fan has no club". Here anything that is not a positive integer answers `null`, so
 * "no club" and "a value we cannot use" are at least the same answer rather than a wrong one — and
 * `0`, which is not a club id, can never be returned.
 *
 * @param {Object|null} claims ID-token claims.
 * @return {number|null} The club id, or null.
 */
export function clubIdFrom( claims ) {
	const raw = claims ? claims.team_name : undefined;

	if ( raw === undefined || raw === null || raw === '' ) {
		return null;
	}

	// Number() rather than parseInt(): parseInt('12abc') is 12, and a claim that is partly a name
	// is not a club id. Integer and positive, because 0 is what the old (int) cast produced for
	// every unusable value and is the one answer that must never come back.
	const id = Number( raw );

	return Number.isInteger( id ) && id > 0 ? id : null;
}

/**
 * Claims to an AuthState.
 *
 * **An absent or empty `sub` is "we cannot tell yet", not an identity.** `outlier-stats` learned
 * this the hard way — its consent cache is keyed by this value, and an entry attributed to nobody
 * is the one thing that can answer one fan's question with another fan's record. So a claims object
 * without a usable `sub` answers NO_FAN rather than a half-populated state.
 *
 * It also cannot answer `'unknown'`. `auth0-login.php` wrote that literal into `auth0_user_id` in
 * three places, which is why `toasts/identity.js` has to filter it: two different fans presented the
 * same id. Nothing produces it here, and it is rejected anyway so that a claim set carrying it
 * cannot revive the collision.
 *
 * @param {Object|null} claims ID-token claims, or null.
 * @return {{signedIn: boolean, sub: string|null, email: string|null, clubId: number|null}} The state.
 */
export function toAuthState( claims ) {
	const sub = claims && typeof claims.sub === 'string' ? claims.sub.trim() : '';

	if ( sub === '' || sub === 'unknown' ) {
		return { ...NO_FAN };
	}

	const email = typeof claims.email === 'string' && claims.email !== '' ? claims.email : null;

	return {
		signedIn: true,
		sub,
		email,
		clubId: clubIdFrom( claims ),
	};
}

/**
 * Whether two states describe the same fan.
 *
 * Only `sub` decides. A club change or an email change is the same person, and re-running the
 * account-switch reset for one would throw away a warm cache for nothing.
 *
 * Two signed-out states are the same "fan" — nobody — so a page where nobody ever signs in settles
 * once and fires no identity change at all.
 *
 * @param {Object|null} a One state.
 * @param {Object|null} b The other.
 * @return {boolean} True when both name the same fan, or both name none.
 */
export function sameFan( a, b ) {
	return fanKey( a ) === fanKey( b );
}

/**
 * The stable key for a fan, or '' for nobody.
 *
 * Exported because the account-switch reset persists it: a tab has to be able to tell that the fan
 * who owns its `sessionStorage` is not the fan who just loaded this document. See ./store.js.
 *
 * @param {Object|null} state An AuthState.
 * @return {string} The `sub`, or ''.
 */
export function fanKey( state ) {
	return state && state.signedIn && typeof state.sub === 'string' ? state.sub : '';
}
